<template>
  <div>
    <Header title="Gestion des pays" />
    <div class="pays-view">
      <div class="container">
        <!-- En-tête avec bouton retour -->
        <div class="view-header">
          <router-link to="/parametres" class="back-button">
            <span class="material-icons">arrow_back</span>
            Retour aux paramètres
          </router-link>
          <h1>Gestion des pays</h1>
          <div class="header-actions">
            <button class="btn btn-primary" @click="openAddPaysModal">
              <span class="material-icons">add</span>
              Nouveau pays
            </button>
          </div>
        </div>

        <!-- Section de recherche -->
        <div class="search-section">
          <div class="search-input">
            <span class="material-icons">search</span>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Rechercher un pays..." 
              @input="filterPays"
            />
          </div>
        </div>

        <!-- Liste des pays -->
        <div class="content-container">
          <div v-if="loading" class="loading-indicator">
            <div class="spinner"></div>
            <p>Chargement des pays...</p>
          </div>
          
          <div v-else-if="filteredPays.length === 0" class="no-results">
            <span class="material-icons">search_off</span>
            <p v-if="searchQuery">Aucun pays trouvé pour la recherche "{{ searchQuery }}"</p>
            <p v-else>Aucun pays disponible. Veuillez en ajouter un.</p>
          </div>

          <div v-else class="pays-list">
            <div
              v-for="pays in paginatedPays"
              :key="pays.id"
              class="pays-item"
              :class="{ 'selected': selectedPays && selectedPays.id === pays.id }"
              @click="selectPays(pays)"
            >
              <div class="pays-info">
                <span class="pays-icon material-icons">public</span>
                <div class="pays-details">
                  <h3>{{ pays.name }}</h3>
                  <p>Code: {{ pays.code || 'Non défini' }}</p>
                </div>
              </div>
              <div class="pays-actions">
                <button class="btn-icon" @click.stop="editPays(pays)">
                  <span class="material-icons">edit</span>
                </button>
                <button class="btn-icon" @click.stop="confirmDeletePays(pays)">
                  <span class="material-icons">delete</span>
                </button>
              </div>
            </div>
            
            <!-- Pagination controls -->
            <div class="pagination-controls" v-if="totalPages > 1">
              <button 
                class="pagination-btn" 
                :disabled="currentPage === 1" 
                @click="currentPage = 1"
                title="Première page"
              >
                <span class="material-icons">first_page</span>
              </button>
              
              <button 
                class="pagination-btn" 
                :disabled="currentPage === 1" 
                @click="currentPage--"
                title="Page précédente"
              >
                <span class="material-icons">chevron_left</span>
              </button>
              
              <span class="pagination-info">Page {{ currentPage }} sur {{ totalPages }}</span>
              
              <button 
                class="pagination-btn" 
                :disabled="currentPage === totalPages" 
                @click="currentPage++"
                title="Page suivante"
              >
                <span class="material-icons">chevron_right</span>
              </button>
              
              <button 
                class="pagination-btn" 
                :disabled="currentPage === totalPages" 
                @click="currentPage = totalPages"
                title="Dernière page"
              >
                <span class="material-icons">last_page</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Détails du pays sélectionné -->
        <div v-if="selectedPays" class="pays-details-panel">
          <div class="panel-header">
            <h3>Détails du pays</h3>
            <button class="btn-close" @click="selectedPays = null">
              <span class="material-icons">close</span>
            </button>
          </div>
          <div class="panel-content">
            <div class="detail-field">
              <span class="field-label">Nom:</span>
              <span class="field-value">{{ selectedPays.name }}</span>
            </div>
            <div class="detail-field">
              <span class="field-label">Code:</span>
              <span class="field-value">{{ selectedPays.code || 'Non défini' }}</span>
            </div>
            <div class="detail-field">
              <span class="field-label">Créé le:</span>
              <span class="field-value">{{ formatDate(selectedPays.createdAt) }}</span>
            </div>
            <div class="detail-field">
              <span class="field-label">Modifié le:</span>
              <span class="field-value">{{ formatDate(selectedPays.updatedAt) }}</span>
            </div>
          </div>
          
          <!-- Liste des provinces de ce pays -->
          <div class="panel-section">
            <div class="panel-header-actions">
              <h4>Provinces associées</h4>
              <div class="panel-actions">
                <button class="btn btn-sm btn-primary" @click="openProvinceModal" title="Ajouter une province">
                  <span class="material-icons">add</span>
                  <span>Ajouter</span>
                </button>
              </div>
            </div>
            <div v-if="loadingProvinces" class="loading-indicator">
              <div class="spinner"></div>
              <p>Chargement des provinces...</p>
            </div>
            <div v-else-if="!associatedProvinces.length" class="no-results">
              <p>Aucune province n'est associée à ce pays</p>
              <button class="btn btn-outline-primary btn-sm" @click="openProvinceModal">
                <span class="material-icons">add_circle_outline</span>
                Ajouter une province
              </button>
            </div>
            <div v-else class="provinces-list">
              <div v-for="province in associatedProvinces" :key="province.id" class="province-item">
                <div class="province-info">
                  <div class="province-avatar">
                    <span class="material-icons province-icon">location_city</span>
                  </div>
                  <div class="province-details">
                    <div class="province-name">{{ province.name }}</div>
                    <div class="province-code" v-if="province.code">Code: {{ province.code }}</div>
                  </div>
                </div>
                <div class="province-actions">
                  <button class="btn-icon" @click.stop="editProvince(province)" title="Modifier cette province">
                    <span class="material-icons">edit</span>
                  </button>
                  <button class="btn-icon" @click.stop="confirmDeleteProvince(province)" title="Supprimer cette province">
                    <span class="material-icons">delete</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal pour l'ajout/modification de pays -->
    <dialog ref="paysModal" class="pays-modal">
      <div class="modal-header">
        <h3>{{ currentAction === 'create' ? 'Ajouter un pays' : 'Modifier le pays' }}</h3>
        <button class="btn-close" @click="closeModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <div class="modal-form-container">
          <form @submit.prevent="savePays" id="paysForm" class="centered-form">
            <div class="form-group">
              <label for="nom">Nom du pays*</label>
              <input 
                type="text" 
                id="nom" 
                v-model="formData.name" 
                required 
                placeholder="Ex: France, Belgique, etc."
                :class="{ 'error': formErrors.name }"
              />
              <span class="error-message" v-if="formErrors.name">{{ formErrors.name }}</span>
              <p class="form-help-text">Le nom complet du pays</p>
            </div>
            <div class="form-group">
              <label for="code">Code du pays (2 lettres)*</label>
              <input 
                type="text" 
                id="code" 
                v-model="formData.code" 
                maxlength="2"
                placeholder="Ex: FR, BE, etc."
                :class="{ 'error': formErrors.code }"
              />
              <span class="error-message" v-if="formErrors.code">{{ formErrors.code }}</span>
              <p class="form-help-text">Code ISO du pays (2 caractères)</p>
            </div>
          </form>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeModal" :disabled="savingPays">Annuler</button>
        <button class="btn btn-primary" form="paysForm" :disabled="savingPays">
          <div v-if="savingPays" class="btn-spinner"></div>
          <span v-if="savingPays">Traitement en cours...</span>
          <span v-else>{{ currentAction === 'create' ? 'Ajouter' : 'Enregistrer' }}</span>
        </button>
      </div>
    </dialog>

    <!-- Modal pour l'ajout/modification de province -->
    <dialog ref="provinceModal" class="province-modal">
      <div class="modal-header">
        <h3>{{ currentProvinceAction === 'create' ? 'Ajouter une province' : 'Modifier la province' }}</h3>
        <button class="btn-close" @click="closeProvinceModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <div class="modal-form-container">
          <form @submit.prevent="saveProvince" id="provinceForm" class="centered-form">
            <div class="form-group">
              <label for="province-nom">Nom de la province*</label>
              <input 
                type="text" 
                id="province-nom" 
                v-model="provinceFormData.name" 
                required 
                placeholder="Ex: Île-de-France, Bruxelles, etc."
                :class="{ 'error': provinceFormErrors.name }"
              />
              <span class="error-message" v-if="provinceFormErrors.name">{{ provinceFormErrors.name }}</span>
              <p class="form-help-text">Le nom complet de la province</p>
            </div>
            <div class="form-group">
              <label for="province-code">Code de la province</label>
              <input 
                type="text" 
                id="province-code" 
                v-model="provinceFormData.code" 
                maxlength="5"
                placeholder="Ex: IDF, BRX, etc."
                :class="{ 'error': provinceFormErrors.code }"
              />
              <span class="error-message" v-if="provinceFormErrors.code">{{ provinceFormErrors.code }}</span>
              <p class="form-help-text">Code unique de la province (optionnel)</p>
            </div>
            <input type="hidden" v-model="provinceFormData.pays_id">
          </form>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeProvinceModal" :disabled="savingProvince">Annuler</button>
        <button class="btn btn-primary" form="provinceForm" :disabled="savingProvince">
          <div v-if="savingProvince" class="btn-spinner"></div>
          <span v-if="savingProvince">Traitement en cours...</span>
          <span v-else>{{ currentProvinceAction === 'create' ? 'Ajouter' : 'Modifier' }}</span>
        </button>
      </div>
    </dialog>

    <!-- Modal de confirmation de suppression de pays -->
    <dialog ref="confirmDeleteModal" class="confirm-modal">
      <div class="modal-header">
        <h3>Confirmer la suppression</h3>
        <button class="btn-close" @click="closeConfirmModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <p>Êtes-vous sûr de vouloir supprimer le pays "{{ paysToDelete?.name }}" ?</p>
        <p class="warning">Cette action est irréversible et peut affecter toutes les provinces et villes associées.</p>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeConfirmModal" :disabled="deletingPays">Annuler</button>
        <button class="btn btn-danger" @click="deletePays" :disabled="deletingPays">
          <div v-if="deletingPays" class="btn-spinner"></div>
          <span v-if="deletingPays">Suppression en cours...</span>
          <span v-else>Supprimer</span>
        </button>
      </div>
    </dialog>

    <!-- Modal de confirmation de suppression de province -->
    <dialog ref="confirmProvinceModal" class="confirm-modal">
      <div class="modal-header">
        <h3>Confirmer la suppression</h3>
        <button class="btn-close" @click="closeConfirmProvinceModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <p>Êtes-vous sûr de vouloir supprimer la province "{{ provinceToDelete?.name }}" ?</p>
        <p class="warning">Cette action est irréversible et peut affecter les villes associées à cette province.</p>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeConfirmProvinceModal" :disabled="deletingProvince">Annuler</button>
        <button class="btn btn-danger" @click="deleteProvince" :disabled="deletingProvince">
          <div v-if="deletingProvince" class="btn-spinner"></div>
          <span v-if="deletingProvince">Suppression en cours...</span>
          <span v-else>Supprimer</span>
        </button>
      </div>
    </dialog>

    <!-- Notification toast -->
    <div 
      v-if="notification.show" 
      class="notification" 
      :class="notification.type"
    >
      <div class="notification-icon">
        <span class="material-icons">
          {{ notification.type === 'success' ? 'check_circle' : 
             notification.type === 'error' ? 'error' : 
             notification.type === 'info' ? 'info' : 'warning' }}
        </span>
      </div>
      <div class="notification-content">
        <h4>{{ notification.title }}</h4>
        <p>{{ notification.message }}</p>
      </div>
      <button class="notification-close" @click="notification.show = false">
        <span class="material-icons">close</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import api from '../services/api';
import Header from '../components/Header.vue';

// Router pour la navigation
const router = useRouter();

// États réactifs
const pays = ref([]);
const filteredPays = ref([]);
const selectedPays = ref(null);
const associatedProvinces = ref([]);
const loading = ref(false);
const loadingProvinces = ref(false);
const savingPays = ref(false);
const deletingPays = ref(false);
const savingProvince = ref(false);
const deletingProvince = ref(false);
const searchQuery = ref('');

// Modals
const paysModal = ref(null);
const provinceModal = ref(null);
const confirmDeleteModal = ref(null);
const confirmProvinceModal = ref(null);

// Province à supprimer
const provinceToDelete = ref(null);

// État du formulaire
const formData = reactive({
  name: '',
  code: ''
});
const formErrors = reactive({
  name: '',
  code: ''
});

// État de l'action courante (create, update, delete)
const currentAction = ref('create');
const currentProvinceAction = ref('create');

// État du formulaire province
const provinceFormData = reactive({
  id: null,
  name: '',
  code: '',
  pays_id: null
});

const provinceFormErrors = reactive({
  name: '',
  code: ''
});
const paysToDelete = ref(null);

// Notification
const notification = reactive({
  show: false,
  type: 'info',
  title: '',
  message: '',
  duration: 5000
});

// Pagination
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalPages = computed(() => Math.ceil(filteredPays.value.length / itemsPerPage.value));

// Liste paginée des pays
const paginatedPays = computed(() => {
  const startIndex = (currentPage.value - 1) * itemsPerPage.value;
  const endIndex = startIndex + itemsPerPage.value;
  return filteredPays.value.slice(startIndex, endIndex);
});

// Charger les pays au montage du composant
onMounted(async () => {
  await loadPays();
});

// Méthodes
async function loadPays() {
  loading.value = true;
  try {
    const response = await api.getPays();
    if (response.data && response.data.success) {
      pays.value = response.data.data || [];
      filteredPays.value = [...pays.value];
    }
  } catch (error) {
    console.error('Erreur lors du chargement des pays:', error);
    showNotification('error', 'Erreur', 'Impossible de charger les pays.');
  } finally {
    loading.value = false;
  }
}

function filterPays() {
  if (!searchQuery.value.trim()) {
    filteredPays.value = [...pays.value];
    return;
  }
  
  const query = searchQuery.value.toLowerCase();
  filteredPays.value = pays.value.filter(pays => {
    const nomMatch = pays.name.toLowerCase().includes(query);
    const codeMatch = pays.code && pays.code.toLowerCase().includes(query);
    
    return nomMatch || codeMatch;
  });
}

async function selectPays(paysItem) {
  selectedPays.value = paysItem;
  await loadAssociatedProvinces(paysItem.id);
}

async function loadAssociatedProvinces(paysId) {
  loadingProvinces.value = true;
  associatedProvinces.value = [];
  
  try {
    const response = await api.get(`/api/parametres/countries/${paysId}/provinces`);
    if (response.data && response.data.success) {
      associatedProvinces.value = response.data.data || [];
      if (associatedProvinces.value.length === 0) {
        showNotification('info', 'Information', 'Aucune province associée à ce pays.');
      }
    } else {
      showNotification('warning', 'Attention', response.data.message || 'Aucune province associée trouvée');
    }
  } catch (error) {
    console.error('Erreur lors du chargement des provinces associées:', error);
    showNotification('error', 'Erreur', 'Impossible de charger les provinces associées.');
  } finally {
    loadingProvinces.value = false;
  }
}

function openAddPaysModal() {
  resetForm();
  currentAction.value = 'create';
  paysModal.value.showModal();
}

function editPays(paysItem) {
  resetForm();
  currentAction.value = 'update';
  formData.name = paysItem.name;
  formData.code = paysItem.code || '';
  formData.id = paysItem.id;
  paysModal.value.showModal();
}

function confirmDeletePays(paysItem) {
  paysToDelete.value = paysItem;
  confirmDeleteModal.value.showModal();
}

function closeModal() {
  paysModal.value.close();
}

function closeConfirmModal() {
  confirmDeleteModal.value.close();
}

function resetForm() {
  formData.id = null;
  formData.name = '';
  formData.code = '';
  Object.keys(formErrors).forEach(key => formErrors[key] = '');
}

async function savePays() {
  // Valider le formulaire
  if (!validateForm()) return;
  
  savingPays.value = true;
  
  try {
    let response;
    
    if (currentAction.value === 'create') {
      response = await api.createPays(formData);
      if (response.data && response.data.success) {
        showNotification('success', 'Succès', 'Pays créé avec succès');
      }
    } else {
      response = await api.updatePays(formData.id, formData);
      if (response.data && response.data.success) {
        showNotification('success', 'Succès', 'Pays modifié avec succès');
      }
    }
    
    await loadPays();
    closeModal();
    
    // Si un pays est sélectionné et qu'il vient d'être modifié, mettre à jour sa sélection
    if (selectedPays.value && selectedPays.value.id === formData.id) {
      const updatedPays = pays.value.find(p => p.id === formData.id);
      if (updatedPays) {
        selectedPays.value = updatedPays;
      }
    }
    
  } catch (error) {
    console.error('Erreur lors de la sauvegarde du pays:', error);
    showNotification('error', 'Erreur', `Impossible de sauvegarder le pays: ${error.message}`);
  } finally {
    savingPays.value = false;
  }
}

async function deletePays() {
  if (!paysToDelete.value) return;
  
  deletingPays.value = true;
  
  try {
    const response = await api.deletePays(paysToDelete.value.id);
    if (response.data && response.data.success) {
      showNotification('success', 'Suppression', 'Pays supprimé avec succès');
      
      // Si le pays supprimé était sélectionné, désélectionner
      if (selectedPays.value && selectedPays.value.id === paysToDelete.value.id) {
        selectedPays.value = null;
      }
      
      await loadPays();
    }
  } catch (error) {
    console.error('Erreur lors de la suppression du pays:', error);
    showNotification('error', 'Erreur', `Impossible de supprimer le pays: ${error.message}`);
  } finally {
    deletingPays.value = false;
  }
  
  closeConfirmModal();
}

function validateForm() {
  let isValid = true;
  
  // Validation du nom
  if (!formData.name.trim()) {
    formErrors.name = 'Le nom est requis';
    isValid = false;
  } else {
    formErrors.name = '';
  }
  
  // Validation du code
  if (!formData.code.trim()) {
    formErrors.code = 'Le code est requis';
    isValid = false;
  } else if (formData.code.trim().length !== 2) {
    formErrors.code = 'Le code doit contenir exactement 2 caractères';
    isValid = false;
  } else {
    formErrors.code = '';
  }
  
  // Le code est maintenant obligatoire et validé ci-dessus
  
  return isValid;
}

function showNotification(type, title, message) {
  notification.show = true;
  notification.type = type;
  notification.title = title;
  notification.message = message;
  
  // Masquer automatiquement après la durée spécifiée
  setTimeout(() => {
    notification.show = false;
  }, notification.duration);
}

function formatDate(dateString) {
  if (!dateString) return 'Non défini';
  
  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  
  return new Date(dateString).toLocaleDateString('fr-FR', options);
}

// Fonctions pour la gestion des provinces
function openProvinceModal() {
  if (!selectedPays.value) {
    showNotification('warning', 'Attention', 'Veuillez d\'abord sélectionner un pays');
    return;
  }
  
  resetProvinceForm();
  currentProvinceAction.value = 'create';
  provinceFormData.pays_id = selectedPays.value.id;
  provinceModal.value.showModal();
}

function closeProvinceModal() {
  provinceModal.value.close();
}

function resetProvinceForm() {
  provinceFormData.id = null;
  provinceFormData.name = '';
  provinceFormData.code = '';
  provinceFormData.pays_id = selectedPays.value ? selectedPays.value.id : null;
  Object.keys(provinceFormErrors).forEach(key => provinceFormErrors[key] = '');
}

function editProvince(province) {
  resetProvinceForm();
  currentProvinceAction.value = 'update';
  provinceFormData.id = province.id;
  provinceFormData.name = province.name;
  provinceFormData.code = province.code || '';
  provinceFormData.pays_id = province.pays_id || selectedPays.value.id;
  provinceModal.value.showModal();
}

function confirmDeleteProvince(province) {
  provinceToDelete.value = province;
  confirmProvinceModal.value.showModal();
}

function closeConfirmProvinceModal() {
  confirmProvinceModal.value.close();
}

function validateProvinceForm() {
  let isValid = true;
  
  // Validation du nom
  if (!provinceFormData.name.trim()) {
    provinceFormErrors.name = 'Le nom est requis';
    isValid = false;
  } else {
    provinceFormErrors.name = '';
  }
  
  // Validation du code (optionnel mais doit être de 5 lettres maximum si fourni)
  if (provinceFormData.code && provinceFormData.code.trim()) {
    if (provinceFormData.code.trim().length > 5) {
      provinceFormErrors.code = 'Le code ne doit pas dépasser 5 caractères';
      isValid = false;
    } else {
      provinceFormErrors.code = '';
    }
  }
  
  return isValid;
}

async function saveProvince() {
  // Valider le formulaire
  if (!validateProvinceForm()) return;
  
  savingProvince.value = true;
  
  try {
    // Adapter les données pour le backend (convertir pays_id en country_id)
    const provinceData = {
      ...provinceFormData,
      country_id: provinceFormData.pays_id
    };
    
    // Afficher les données pour debug
    console.log('Données envoyées au serveur:', provinceData);
    
    let response;
    
    if (currentProvinceAction.value === 'create') {
      response = await api.post('/api/parametres/provinces', provinceData);
      if (response.data && response.data.success) {
        showNotification('success', 'Succès', 'Province créée avec succès');
      }
    } else {
      response = await api.put(`/api/parametres/provinces/${provinceFormData.id}`, provinceData);
      if (response.data && response.data.success) {
        showNotification('success', 'Succès', 'Province modifiée avec succès');
      }
    }
    
    // Recharger les provinces associées au pays sélectionné
    if (selectedPays.value) {
      await loadAssociatedProvinces(selectedPays.value.id);
    }
    
    closeProvinceModal();
  } catch (error) {
    console.error('Erreur lors de l\'enregistrement de la province:', error);
    showNotification('error', 'Erreur', `Impossible d'enregistrer la province: ${error.message}`);
  } finally {
    savingProvince.value = false;
  }
}

async function deleteProvince() {
  if (!provinceToDelete.value) return;
  
  deletingProvince.value = true;
  
  try {
    const response = await api.delete(`/api/parametres/provinces/${provinceToDelete.value.id}`);
    
    if (response.data && response.data.success) {
      showNotification('success', 'Succès', 'Province supprimée avec succès');
      
      // Recharger les provinces associées au pays sélectionné
      if (selectedPays.value) {
        await loadAssociatedProvinces(selectedPays.value.id);
      }
    }
  } catch (error) {
    console.error('Erreur lors de la suppression de la province:', error);
    showNotification('error', 'Erreur', `Impossible de supprimer la province: ${error.message}`);
  } finally {
    deletingProvince.value = false;
  }
  
  closeConfirmProvinceModal();
}

</script>

<style scoped>
.pays-view {
  padding: 20px;
  width: 100%;
  box-sizing: border-box;
  margin: 0 auto;
}

.container {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 20px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  width: 100%;
  box-sizing: border-box;
}

/* Tablettes et petits écrans */
@media (min-width: 768px) and (max-width: 991px) {
  .container {
    grid-template-columns: 1fr;
    padding: 15px;
  }
}

/* Écrans moyens et grands */
@media (min-width: 992px) {
  .container {
    grid-template-columns: 2fr 1fr;
  }
  
  .view-header, .search-section, .loading-indicator, .no-results {
    grid-column: span 2;
  }
}

/* Très petits écrans (mobiles) */
@media (max-width: 767px) {
  .pays-view {
    padding: 10px;
  }
  
  .container {
    padding: 15px;
    border-radius: 6px;
  }
  
  .view-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-actions {
    width: 100%;
    margin-top: 10px;
  }
  
  .pays-list, .panel-content {
    max-height: none;
  }
}

/* En-tête */
.view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.view-header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.back-button {
  display: flex;
  align-items: center;
  color: var(--turquoise-primary);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

.back-button .material-icons {
  margin-right: 5px;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* Section de recherche */
.search-section {
  margin-bottom: 20px;
}

.search-input {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 10px 15px;
}

.search-input .material-icons {
  color: var(--turquoise-primary);
  margin-right: 10px;
}

.search-input input {
  border: none;
  background: transparent;
  flex: 1;
  font-size: 16px;
  outline: none;
}

/* Liste des pays */
.pays-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pays-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-radius: 8px;
  background-color: #f9f9f9;
  border-left: 4px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pays-item:hover {
  background-color: rgba(48, 199, 181, 0.05);
}

.pays-item.selected {
  border-left-color: var(--turquoise-primary);
  background-color: var(--turquoise-pale);
}

.pays-info {
  display: flex;
  align-items: center;
}

.pays-icon {
  color: var(--turquoise-primary);
  margin-right: 15px;
  font-size: 24px;
}

.pays-details h3 {
  margin: 0 0 5px;
  font-size: 16px;
  font-weight: 500;
}

.pays-details p {
  margin: 0;
  color: #777;
  font-size: 14px;
}

.pays-actions {
  display: flex;
  gap: 10px;
}

.btn-icon {
  border: none;
  background: none;
  color: #777;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.btn-icon:hover {
  background-color: rgba(48, 199, 181, 0.1);
  color: var(--turquoise-deep);
}

/* Panneau de détails */
.pays-details-panel {
  background-color: #f9f9f9;
  border-radius: 8px;
  overflow: hidden;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--turquoise-pale);
  padding: 15px;
  border-bottom: 1px solid #ddd;
}

.panel-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}

.panel-content {
  padding: 15px;
}

.detail-field {
  margin-bottom: 12px;
}

.field-label {
  display: block;
  font-size: 14px;
  color: #777;
  margin-bottom: 3px;
}

.field-value {
  font-size: 16px;
  color: #333;
}

.panel-section {
  padding: 15px;
  border-top: 1px solid #ddd;
}

.panel-section h4 {
  margin: 0 0 15px;
  font-size: 16px;
  font-weight: 500;
}

/* Liste des provinces */
.provinces-list {
  max-height: 300px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.province-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.province-info {
  display: flex;
  align-items: center;
}

.province-info .material-icons {
  margin-right: 10px;
  font-size: 20px;
  color: var(--turquoise-dark);
}

/* Indicateur de chargement */
.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid var(--turquoise-primary);
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translate(-50%, -48%) scale(0.98); }
  to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}

/* Pas de résultats */
.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  color: #777;
}

.no-results .material-icons {
  font-size: 48px;
  margin-bottom: 15px;
  color: var(--turquoise-primary);
  opacity: 0.6;
}

/* Modal */
.pays-modal, .confirm-modal, .province-modal {
  border: none;
  border-radius: 10px;
  padding: 0;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25), 0 5px 15px rgba(0, 0, 0, 0.15);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  opacity: 1;
  transition: opacity 0.3s ease, transform 0.3s ease;
  animation: fadeIn 0.3s ease-out;
  overflow: hidden;
}

.pays-modal::before, .confirm-modal::before, .province-modal::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
}

.pays-modal::backdrop, .confirm-modal::backdrop, .province-modal::backdrop {
  background-color: rgba(0, 0, 0, 0.6);
  transition: background-color 0.3s ease;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
  border-radius: 10px 10px 0 0;
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: white;
  letter-spacing: 0.2px;
}

.modal-body {
  padding: 24px;
  background-color: #ffffff;
  max-height: 60vh;
  overflow-y: auto;
}

.modal-form-container {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 8px;
}

.centered-form {
  width: 100%;
  max-width: 400px;
}

.form-help-text {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  margin-bottom: 0;
  font-style: italic;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background-color: #fcfcfc;
  border-radius: 0 0 10px 10px;
}

.btn-close {
  background: none;
  border: none;
  cursor: pointer;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s ease;
  margin: -8px;
}

.btn-close:hover {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  transform: rotate(90deg);
}

/* Formulaire */
.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-group input, .form-group select, .form-group textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-group input:focus, .form-group select:focus, .form-group textarea:focus {
  border-color: var(--turquoise-primary);
  outline: none;
  box-shadow: 0 0 0 3px rgba(48, 199, 181, 0.15);
}

.form-group input.error, .form-group select.error {
  border-color: var(--coral-soft);
}

.error-message {
  color: var(--coral-soft);
  font-size: 12px;
  margin-top: 5px;
}

/* Boutons */
.btn {
  padding: 10px 18px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  letter-spacing: 0.3px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: btn-spin 1s linear infinite;
  margin-right: 4px;
}

@keyframes btn-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.btn .material-icons {
  font-size: 20px;
}

.btn-primary {
  background-color: var(--turquoise-primary);
  color: white;
  border: 1px solid var(--turquoise-primary);
  position: relative;
  overflow: hidden;
}

.btn-primary:hover {
  background-color: var(--turquoise-deep);
  box-shadow: 0 4px 8px rgba(48, 199, 181, 0.2);
  transform: translateY(-1px);
}

.btn-primary:active {
  transform: translateY(1px);
  box-shadow: 0 2px 3px rgba(48, 199, 181, 0.2);
}

.btn-secondary {
  background-color: #f8f9fa;
  color: #444;
  border: 1px solid #e0e0e0;
}

.btn-secondary:hover {
  background-color: #f0f0f0;
  border-color: #d0d0d0;
  color: #333;
}

.btn-secondary:active {
  background-color: #e9ecef;
  transform: translateY(1px);
}

.btn-danger {
  background-color: var(--coral-soft);
  color: white;
  border: 1px solid var(--coral-soft);
}

.btn-danger:hover {
  background-color: #ff6a56;
  box-shadow: 0 4px 8px rgba(255, 122, 104, 0.2);
  transform: translateY(-1px);
}

.btn-danger:active {
  transform: translateY(1px);
  box-shadow: 0 2px 3px rgba(255, 122, 104, 0.2);
}

/* Notification */
.notification {
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  max-width: 400px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 15px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  z-index: 1000;
}

.notification.success {
  border-left: 4px solid var(--turquoise-primary);
}

.notification.error {
  border-left: 4px solid var(--coral-soft);
}

.notification.warning {
  border-left: 4px solid #FF9800;
}

.notification.info {
  border-left: 4px solid var(--turquoise-dark);
}

.notification-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification.success .material-icons {
  color: var(--turquoise-primary);
}

.notification.error .material-icons {
  color: var(--coral-soft);
}

.notification.warning .material-icons {
  color: #FF9800;
}

.notification.info .material-icons {
  color: var(--turquoise-dark);
}

.notification-content {
  flex: 1;
}

.notification-content h4 {
  margin: 0 0 5px;
  font-size: 16px;
  font-weight: 600;
}

.notification-content p {
  margin: 0;
  font-size: 14px;
  color: #555;
}

.notification-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -5px;
}

.notification-close:hover {
  color: #333;
}

/* Personnalisation des barres de défilement */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: var(--turquoise-primary);
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--turquoise-deep);
}

/* Pour Firefox */
* {
  scrollbar-width: thin;
  scrollbar-color: var(--turquoise-primary) #f1f1f1;
}

.warning {
  color: var(--coral-soft);
  font-weight: 500;
}

/* Styles de pagination */
.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background-color: #fff;
  border-radius: 50%;
  margin: 0 5px;
  cursor: pointer;
  color: #7B1FA2;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #f3e5f5;
  box-shadow: 0 3px 6px rgba(123, 31, 162, 0.2);
  transform: translateY(-1px);
}

.pagination-btn:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.pagination-btn:disabled {
  background-color: #f0f0f0;
  color: #ccc;
  cursor: not-allowed;
  box-shadow: none;
}

.pagination-info {
  margin: 0 12px;
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

/* Responsive design pour pagination */
@media (max-width: 767px) {
  .pagination-controls {
    flex-wrap: wrap;
    padding: 8px 5px;
  }
  
  .pagination-btn {
    width: 32px;
    height: 32px;
    margin: 0 3px;
  }
  
  .pagination-info {
    margin: 0 8px;
    font-size: 13px;
  }
}
</style>
